import { Link } from "react-router-dom";
import { usePageMeta } from "../../hooks/usePageMeta";
import { ARTICLES } from "../../mocks/articles";
import { getCategory } from "../../mocks/site";
import { formatCount } from "../../utils/format";
import Breadcrumb from "../../components/feature/Breadcrumb";

const TRENDS = ["up", "up", "down", "up", "down", "up", "down", "down", "up", "up", "down", "up"];

const RANK_STYLE = [
  "bg-primary-500 text-background-50",
  "bg-accent-500 text-background-50",
  "bg-secondary-500 text-background-50",
  "bg-secondary-200 text-foreground-700",
  "bg-secondary-200 text-foreground-700",
  "bg-secondary-200 text-foreground-700",
];

export default function Hot() {
  usePageMeta({
    title: "热门话题榜 · 今日热度最高的话题",
    description:
      "吃瓜17热门话题榜，汇总当前热度最高的娱乐话题与事件，按综合热度排序，实时反映影视、综艺、明星与全网热梗的讨论热度走势。",
    keywords: "吃瓜17,热门话题,热搜榜,今日热度,娱乐热点,热梗排行",
    path: "/hot",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "吃瓜17 热门话题榜",
        url: "https://xttkax.cn/hot",
        numberOfItems: ARTICLES.length,
        itemListElement: [...ARTICLES].sort((a, b) => b.heat - a.heat).slice(0, 10).map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: a.title,
          url: `https://xttkax.cn/news/${a.id}`,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [{ "@type": "ListItem", position: 1, name: "今日首页", item: "https://xttkax.cn/" }, { "@type": "ListItem", position: 2, name: "热门话题" }],
      },
    ],
  });

  const ranked = [...ARTICLES].sort((a, b) => b.heat - a.heat);

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="bg-accent-50 py-10 md:py-14">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <Breadcrumb items={[{ label: "今日首页", to: "/" }, { label: "热门话题" }]} />
          <div className="mt-4 flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-4 py-1.5 text-sm font-semibold text-background-50">
                <i className="ri-fire-fill" aria-hidden="true"></i>实时更新
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">热门话题榜</h1>
              <p className="mt-3 max-w-2xl text-foreground-700">谁在霸榜、谁在升温？综合阅读与讨论热度，一次看清今天大家在围观什么。</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-background-50 px-5 py-3 text-center">
                <p className="font-heading text-2xl font-bold text-primary-600">{ranked.length}</p>
                <p className="text-xs text-foreground-500">今日上榜</p>
              </div>
              <div className="rounded-2xl bg-background-50 px-5 py-3 text-center">
                <p className="font-heading text-2xl font-bold text-accent-600">98</p>
                <p className="text-xs text-foreground-500">最高热度</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ol className="flex flex-col gap-3">
              {ranked.map((a, i) => {
                const cat = getCategory(a.category);
                const trend = TRENDS[i % TRENDS.length];
                const rise = trend === "up";
                return (
                  <li key={a.id}>
                    <Link
                      to={`/news/${a.id}`}
                      className="group flex items-center gap-4 rounded-2xl border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-200 md:p-4"
                    >
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-heading text-lg font-bold ${RANK_STYLE[i] ?? "bg-secondary-200 text-foreground-700"}`}>
                        {i + 1}
                      </span>
                      <img src={a.cover} alt={a.coverAlt} className="hidden h-16 w-24 shrink-0 rounded-lg object-cover sm:block" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-xs">
                          {cat && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-foreground-600">
                              <i className={cat.icon} aria-hidden="true"></i>{cat.name}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-foreground-500"><i className="ri-eye-line" aria-hidden="true"></i>{formatCount(a.views)}</span>
                        </div>
                        <h2 className="mt-1.5 line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-base">{a.title}</h2>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="font-heading text-lg font-bold text-primary-600">{a.heat}</span>
                        <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs ${rise ? "text-primary-700" : "text-foreground-500"}`}>
                          <i className={rise ? "ri-arrow-up-line" : "ri-arrow-down-line"} aria-hidden="true"></i>
                          {rise ? "升温" : "回落"}
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <section className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-question-line text-primary-500" aria-hidden="true"></i>热度是怎么算的？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                热度值综合阅读量、讨论量、转发与二创活跃度等维度加权计算，反映一条内容近期的讨论集中度，仅作参考，不构成对任何作品或艺人的主观评价。
              </p>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-primary-500"></span>霸榜话题多为内容自带的自然讨论</li>
                <li className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-accent-500"></span>榜单每小时滚动更新，非固定排序</li>
                <li className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-secondary-400"></span>点击任意条目即可阅读完整资讯</li>
              </ul>
            </section>
            <section className="rounded-2xl bg-secondary-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-compass-3-line text-primary-600" aria-hidden="true"></i>想按栏目围观？
              </h2>
              <p className="mt-2 text-sm text-foreground-600">去资讯速递页按栏目筛选，看不同分类的最新内容。</p>
              <Link to="/news" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-article-line" aria-hidden="true"></i>前往资讯速递
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}